import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { MsalProvider } from '@/providers/MsalProvider';
import { initAuth } from '@/stores/authStore';
import App from './App';
import { ErrorBoundary } from '@/components/ErrorBoundary';
// Mapbox's stylesheet, here and first, though only the map components use it.
// Its rules have the same specificity as Tailwind's utilities (one class each),
// so whichever loads LATER wins a tie — a map container's `absolute` against
// Mapbox's `.mapboxgl-map { position: relative }`, say. Imported from the map
// components alone, it would ship with their on-demand page files and load
// after this stylesheet, reversing every such tie. Here it keeps the order the
// app has always had: Mapbox first, Tailwind over it.
import 'mapbox-gl/dist/mapbox-gl.css';
// Self-hosted variable fonts — the same Inter the sibling apps ship.
import '@fontsource-variable/inter/index.css';
import '@fontsource-variable/jetbrains-mono/index.css';
import './styles/globals.css';

// Auth lives at module level for the lifetime of the tab — see initAuth()
initAuth();

// Safari restores tabs from the back/forward cache (bfcache) with their JS heap
// frozen in place. If Flow was bfcached mid-load (e.g. while still showing
// "Verifying access…"), the restored page resumes with dead in-flight requests
// that never settle, so the spinner hangs until a full reload. Reloading on a
// persisted restore reproduces the manual fix (re-entering the URL) and
// guarantees a clean boot. It only fires for true bfcache restores, so normal
// in-app navigation is unaffected.
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if ((event as PageTransitionEvent).persisted) {
      window.location.reload();
    }
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <MsalProvider>
        <ErrorBoundary>
          <App />
        </ErrorBoundary>
      </MsalProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
